import { Outlet, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useProperty } from "@/context/PropertyContext";
import { useBilling } from "@/context/BillingContext";
import { useTranslation } from "react-i18next";
import FoxLogo from "@/components/FoxLogo";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import MobileTabBar from "@/components/MobileTabBar";
import MenuSheet from "@/components/MenuSheet";
import { ChevronDown, AlertTriangle } from "lucide-react";
import { useState } from "react";

function BillingBanner() {
  const { t } = useTranslation();
  const { status } = useBilling();
  if (!status) return null;
  if (status.status === "active") return null;
  const isBlocked = status.blocked;
  const trialEnd = status.trial_end_at ? new Date(status.trial_end_at) : null;
  const daysLeft = trialEnd ? Math.max(0, Math.ceil((trialEnd - new Date()) / 86400000)) : 0;
  const total = status?.billing?.total ?? 0;

  // Caso bloccato: banner rosso forte, CTA bianca, ricorda Alloggiati attivo.
  if (isBlocked) {
    return (
      <div className="bg-red-800 text-red-50 border-b-2 border-red-900" data-testid="billing-banner">
        <div className="px-6 py-3 flex flex-wrap items-center gap-3">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" strokeWidth={2} />
          <div className="flex-1 text-sm leading-snug">
            <strong>{t("billing_banner.suspended_title")}</strong>{" "}
            {t("billing_banner.suspended_body", { amount: total.toFixed(2) })}
            <span className="ml-2 text-xs text-red-200">{t("billing_banner.alloggiati_note")}</span>
          </div>
          <a href="/app/billing" data-testid="billing-banner-cta"
            className="inline-flex items-center gap-2 bg-white text-red-900 px-4 py-2 text-sm font-medium rounded-sm hover:bg-red-50 whitespace-nowrap">
            {t("billing_banner.pay_now")}
          </a>
        </div>
      </div>
    );
  }

  let msg = null;
  let cls = "bg-blue-50 text-blue-900 border-blue-200";
  if (status.status === "past_due") {
    msg = t("billing_banner.trial_over");
    cls = "bg-amber-50 text-amber-900 border-amber-200";
  } else if (status.status === "trialing" && daysLeft <= 7) {
    msg = t(daysLeft === 1 ? "billing_banner.trial_left_one" : "billing_banner.trial_left_other", { days: daysLeft });
  }
  if (!msg) return null;
  return (
    <div className={`border-b px-6 py-2 text-sm flex items-center justify-between gap-4 ${cls}`} data-testid="billing-banner">
      <span>{msg}</span>
      <a href="/app/billing" className="underline underline-offset-2 whitespace-nowrap">{t("billing_banner.to_billing")}</a>
    </div>
  );
}

export default function Layout() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { properties, current, select } = useProperty();
  const nav = useNavigate();
  const [propOpen, setPropOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // Admin landing on /app would see an empty host dashboard. Redirect to /admin
  // unless explicitly impersonating a host (cookie `impersonated_by` set during impersonation).
  if (user?.role === "admin" && !document.cookie.includes("impersonated_by=")) {
    return <Navigate to="/admin" replace />;
  }

  return (
    <div className="min-h-screen bg-white">
      <ImpersonationBanner />

      {/* ─── Slim top bar (mobile-first, sticky) ───────────────── */}
      <header className="sticky top-0 z-30 bg-white/85 backdrop-blur border-b border-slate-200" data-testid="app-topbar">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-3 px-4 sm:px-6 py-3">
          <button
            onClick={() => { nav("/app"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity cursor-pointer"
            data-testid="topbar-logo"
            aria-label="Vai alla home"
          >
            <FoxLogo size={26} data-testid="fox-logo-sidebar" />
            <span className="font-heading font-semibold text-lg tracking-tight text-slate-900">Xenia<span className="text-blue-600">.</span></span>
          </button>
          <div className="relative flex-1 max-w-xs">
            <button onClick={() => setPropOpen(!propOpen)} data-testid="property-switcher"
              className="w-full flex items-center justify-between border border-slate-200 hover:border-slate-300 bg-slate-50 rounded-full px-3 py-1.5 text-left text-xs transition-colors">
              <div className="truncate">
                <span className="font-medium truncate text-slate-900" data-testid="current-property-name">{current?.name || "—"}</span>
                {current?.comune && <span className="text-slate-500 ml-1.5">· {current.comune}</span>}
              </div>
              <ChevronDown className={`w-3 h-3 text-slate-500 flex-shrink-0 transition-transform ${propOpen ? "rotate-180" : ""}`} />
            </button>
            {propOpen && (
              <div className="absolute z-30 mt-2 inset-x-0 bg-white border border-slate-200 rounded-2xl shadow-xl max-h-64 overflow-auto">
                {properties.map(p => (
                  <button key={p.id} onClick={() => { setPropOpen(false); select(p.id); }}
                    data-testid={`switch-property-${p.id}`}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b border-slate-100 last:border-b-0 transition-colors ${p.id === current?.id ? "bg-blue-50" : ""}`}>
                    <div className="font-medium truncate text-slate-900">{p.name}</div>
                    <div className="text-xs text-slate-500">{p.comune} · {p.type}</div>
                  </button>
                ))}
                <button onClick={() => { setPropOpen(false); nav("/app/properties"); }}
                  className="w-full text-left px-3 py-2.5 text-sm text-blue-600 hover:bg-slate-50 font-medium">+ {t("nav.new_property")}</button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* ─── Main content ────────────────────────────────────── */}
      <main className="max-w-6xl mx-auto pb-32">
        <BillingBanner />
        <Outlet />
      </main>

      {/* ─── Bottom tab bar + slide-up menu ──────────────────── */}
      <MobileTabBar onOpenMenu={() => setMenuOpen(true)} />
      <MenuSheet open={menuOpen} onClose={() => setMenuOpen(false)} />
    </div>
  );
}
